/**
 * DuplicateDealModal — Yes/No confirmation shown when an operator enters a
 * deal # that already exists on another row of this location's nut sheet (same
 * month). Warns about a likely double-entry before the row is filled in.
 *
 * "Yes, re-enter" keeps the deal # and applies the DealerBuilt auto-fill;
 * "No" clears the deal # so the operator doesn't duplicate the deal.
 */

import React from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { INDIVIDUAL_LOCATIONS } from '../LocationDropdown';
import { ExistingNutSheetMatch } from '../../types/nutSheet';

interface DuplicateDealModalProps {
  dealNumber: string;
  matches: ExistingNutSheetMatch[];
  onConfirm: () => void;
  onCancel: () => void;
}

function locationName(locationId: number): string {
  return INDIVIDUAL_LOCATIONS.find((l) => l.id === locationId)?.name ?? `Location ${locationId}`;
}

function formatDate(date: string): string {
  const [y, m, d] = date.split('-').map(Number);
  const dt = new Date(y, (m ?? 1) - 1, d ?? 1);
  return dt.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

export const DuplicateDealModal: React.FC<DuplicateDealModalProps> = ({
  dealNumber,
  matches,
  onConfirm,
  onCancel,
}) => {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <div className="flex items-start gap-3">
          <ExclamationTriangleIcon className="h-6 w-6 text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Deal # already entered</h3>
            <p className="mt-2 text-sm text-gray-700">
              Deal&nbsp;# <span className="font-semibold">{dealNumber}</span> has already been
              entered on:
            </p>
            <ul className="mt-2 space-y-1 text-sm text-gray-800">
              {matches.map((m) => (
                <li key={m.entryId} className="flex flex-wrap gap-x-1">
                  <span className="font-medium">{locationName(m.locationId)}</span>
                  <span>—</span>
                  <span>{formatDate(m.date)}</span>
                  {m.customerLastName && (
                    <span className="text-gray-500">({m.customerLastName})</span>
                  )}
                </li>
              ))}
            </ul>
            <p className="mt-3 text-sm text-gray-700">
              Are you sure you want to re-enter it?
            </p>
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            No
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 text-sm font-medium rounded-md bg-amber-600 text-white hover:bg-amber-700"
          >
            Yes, re-enter
          </button>
        </div>
      </div>
    </div>
  );
};
